import { CheckOutlined } from "@ant-design/icons";
import { Button, Input, message, Spin } from "antd";
import { useCallback, useEffect, useState } from "react";
import { useNavigate, useOutletContext } from "react-router-dom";
import { PageHeader } from "@/shared/components/ui/PageHeader";
import type { AppRole } from "@/shared/config/navigation";
import { canManagePaymentSettings } from "@/shared/config/permissions";
import { PaymentsNavigation } from "../components/PaymentsNavigation";
import {
	type FrontendBankAccount,
	getBankAccount,
	saveBankAccount,
} from "../services/bank-accounts.api";

const emptyForm: FrontendBankAccount = {
	accountNumber: "",
	bankName: "",
	mfo: "",
	accountName: "",
};

export function BankAccountPage() {
	const navigate = useNavigate();
	const { role } = useOutletContext<{ role: AppRole }>();
	const canManageSettings = canManagePaymentSettings(role);
	const [form, setForm] = useState<FrontendBankAccount>(emptyForm);
	const [loading, setLoading] = useState(true);
	const [saving, setSaving] = useState(false);

	const setField = (key: keyof FrontendBankAccount, value: string) => {
		setForm((current) => ({ ...current, [key]: value }));
	};

	useEffect(() => {
		let cancelled = false;
		setLoading(true);
		getBankAccount()
			.then((account) => {
				if (cancelled) {
					return;
				}
				if (account) {
					setForm(account);
				}
			})
			.catch((error) => {
				console.error("Failed to load bank account:", error);
				message.error("Bank rekvizitlarini yuklashda xatolik yuz berdi");
			})
			.finally(() => {
				if (!cancelled) {
					setLoading(false);
				}
			});
		return () => {
			cancelled = true;
		};
	}, []);

	const handleSave = useCallback(async () => {
		if (!canManageSettings) {
			message.warning("Bu rol bank rekvizitlarini o'zgartira olmaydi.");
			return;
		}

		setSaving(true);
		try {
			const saved = await saveBankAccount(form);
			setForm(saved);
			message.success("Bank akkount saqlandi");
		} catch {
			message.error("Bank akkountni saqlashda xatolik yuz berdi");
		} finally {
			setSaving(false);
		}
	}, [canManageSettings, form]);

	return (
		<div className="crm-bank-account-page">
			<PageHeader
				title="Bank rekvizitlari"
				description="Shartnomada ko'rsatiladigan tashkilot rekvizitlari"
			/>

			<PaymentsNavigation activeTab="bank" role={role} />

			<div className="crm-card crm-bank-card">
				<div className="crm-card-head">
					<h3>Rekvizitlar</h3>
				</div>
				<div className="crm-card-body">
					{loading ? (
						<div style={{ textAlign: "center", padding: "32px 0" }}>
							<Spin />
						</div>
					) : (
						<>
							<div className="crm-sale-field">
								<span>Hisob raqami</span>
								<Input
									value={form.accountNumber}
									onChange={(event) => setField("accountNumber", event.target.value)}
								/>
							</div>

							<div className="crm-payment-two-col">
								<div className="crm-sale-field">
									<span>Bank nomi</span>
									<Input
										value={form.bankName}
										onChange={(event) => setField("bankName", event.target.value)}
									/>
								</div>
								<div className="crm-sale-field">
									<span>MFO</span>
									<Input
										value={form.mfo}
										onChange={(event) => setField("mfo", event.target.value)}
									/>
								</div>
							</div>

							<div className="crm-sale-field">
								<span>Hisob nomi</span>
								<Input
									value={form.accountName}
									onChange={(event) => setField("accountName", event.target.value)}
								/>
							</div>

							<div className="crm-payment-rule">
								<span>
									To'lov yozuvlari bu jadvalga bog'liq emas. Rekvizitlar faqat shartnoma va hujjat
									generatsiyasida ishlatiladi.
								</span>
							</div>

							<div className="crm-form-actions">
								<Button onClick={() => navigate("/payments")}>Bekor</Button>
								<Button
									className="crm-primary-btn"
									type="primary"
									icon={<CheckOutlined />}
									loading={saving}
									onClick={handleSave}
								>
									Saqlash
								</Button>
							</div>
						</>
					)}
				</div>
			</div>
		</div>
	);
}

export default BankAccountPage;
